/* @flow @format */

import type { Reducer } from 'redux';

import { type ActionType, createAction } from '../utils';
import type { CustomSelector, FailurePayload } from '../types';
import * as L from 'partial.lenses';

type Progress = {
  contestUid: string,
  courseUid: string,
  progress: number,
};

export type Progresses = Array<Progress>;

// Actions

const PROGRESS_UPDATED: 'proc/progresses/PROGRESS_UPDATED' = 'proc/progresses/PROGRESS_UPDATED';
const FETCH_PROGRESS_BEGIN: 'proc/auth/FETCH_PROGRESS_BEGIN' = 'proc/auth/FETCH_PROGRESS_BEGIN';
const FETCH_PROGRESS_SUCCESS: 'proc/auth/FETCH_PROGRESS_SUCCESS' = 'proc/auth/FETCH_PROGRESS_SUCCESS';
const FETCH_PROGRESS_FAILURE: 'proc/auth/FETCH_PROGRESS_FAILURE' = 'proc/auth/FETCH_PROGRESS_FAILURE';
const UPLOAD_PROGRESS_BEGIN: 'proc/auth/UPLOAD_PROGRESS_BEGIN' = 'proc/auth/UPLOAD_PROGRESS_BEGIN';
const UPLOAD_PROGRESS_SUCCESS: 'proc/auth/UPLOAD_PROGRESS_SUCCESS' = 'proc/auth/UPLOAD_PROGRESS_SUCCESS';
const UPLOAD_PROGRESS_FAILURE: 'proc/auth/UPLOAD_PROGRESS_FAILURE' = 'proc/auth/UPLOAD_PROGRESS_FAILURE';

type ProgressUpdatedInput = {| +contestUid: string, +courseUid: string, +progress: number |};
type FetchFailurePayload = FailurePayload<void>;
type UploadFailurePayload = FailurePayload<void>;

export const progressUpdated = createAction<typeof PROGRESS_UPDATED, ProgressUpdatedInput>(PROGRESS_UPDATED);
export const fetchProgressBegin = createAction<typeof FETCH_PROGRESS_BEGIN, void>(FETCH_PROGRESS_BEGIN);
export const fetchProgressSuccess = createAction<typeof FETCH_PROGRESS_SUCCESS, any>(FETCH_PROGRESS_SUCCESS);
export const fetchProgressFailure = createAction<typeof FETCH_PROGRESS_FAILURE, FetchFailurePayload>(
  FETCH_PROGRESS_FAILURE
);
export const uploadProgressBegin = createAction<typeof UPLOAD_PROGRESS_BEGIN, void>(UPLOAD_PROGRESS_BEGIN);
export const uploadProgressSuccess = createAction<typeof UPLOAD_PROGRESS_SUCCESS, any>(UPLOAD_PROGRESS_SUCCESS);
export const uploadProgressFailure = createAction<typeof UPLOAD_PROGRESS_FAILURE, UploadFailurePayload>(
  UPLOAD_PROGRESS_FAILURE
);

export type ProgressesActionType =
  | ActionType<typeof progressUpdated>
  | ActionType<typeof fetchProgressBegin>
  | ActionType<typeof fetchProgressSuccess>
  | ActionType<typeof fetchProgressFailure>
  | ActionType<typeof uploadProgressBegin>
  | ActionType<typeof uploadProgressSuccess>
  | ActionType<typeof uploadProgressFailure>;

// Reducer

const makeProgressLens = (contestUid: string, courseUid: string) => [
  L.find(p => p.contestUid === contestUid && p.courseUid === courseUid),
  L.valueOr({ contestUid, courseUid, progress: 0 }),
  'progress',
];

const reducer: Reducer<Progresses, ProgressesActionType> = (state = [], action) => {
  switch (action.type) {
    case PROGRESS_UPDATED: {
      const { contestUid, courseUid, progress } = action.payload;
      return L.set(makeProgressLens(contestUid, courseUid), progress, state);
    }
    case FETCH_PROGRESS_SUCCESS:
      return action.payload;
    case FETCH_PROGRESS_BEGIN:
    case FETCH_PROGRESS_FAILURE:
    case UPLOAD_PROGRESS_BEGIN:
    case UPLOAD_PROGRESS_SUCCESS:
    case UPLOAD_PROGRESS_FAILURE:
      // ingore
      return state;
    default:
      /*::(action: empty);*/
      return state;
  }
};

export default reducer;

// Selectors
type Selector<T> = CustomSelector<Progresses, T>;

export const getProgress: Selector<number> = (state, contestUid: string, courseUid: string) =>
  L.get(makeProgressLens(contestUid, courseUid), state);
